// THE NARROW SHELL — a laptop window made phone-narrow (≤760px).
//
// v4.14.8 (Karan 27/10/2026, Renov8 in dark mode: "why are the colours
// different when made smaller than full size? Why is the UI not brought up to
// date for the contractor too?"). A window that STARTS phone-sized runs the
// phone app (public/mobile/, app.js wantsMobile). A window that is made narrow
// AFTER it opened keeps the desktop screen it is on — Catalogue, a quote, the
// timesheet — because the phone app has no such screens, and swapping apps
// under somebody mid-task would throw their work away. This module is the
// chrome that desktop screen gets at that width, and it is now the v4.14 phone's
// chrome, drawn with the phone's own classes (v4/phone.css, scoped by .m-app):
//
//   · the header — Release D PR 7: navInline's glass bar with the company's
//     disc, then a search glass button (ASP: Search or ask Track, the Ask
//     Track bar; a contractor: Search projects) and you (your menu).
//   · the docked tab bar — ASP: Today · Tasks · + Add · Projects · More (the
//     phone's); a contractor: Today · Projects · + Add · Time · More.
//   · + Add — the phone's sheet, each tile pressing the desktop's own control.
//   · More — the phone's inset groups (Release D), the rail's groups, live counts.
//
// It replaced the §13 floating navy glass bar with the "FOR <SCREEN>" Ask disc
// in the middle (the bar Karan saw), whose dark glass was a fixed navy from
// before dark Grey existed. Colours: .p4-desk maps the phone tokens onto the
// desktop's own (tokens/mobile.css), so light, Grey, Black and Navy match the
// full-width app.
//
// This module owns only the chrome. Routing stays in app.js — `onSelect(id)` is
// its `selectView`, and app.js hands in what each + Add tile does.
import { navIcon } from './nav-icons.js';
import { navInline, glassBtn, avatarBtn, companyDisc, group, row, sheetHead } from './mobile/ui.js';

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** The tabs: the v4.14 phone's, by company kind. `view` is the desktop view each opens. */
export const NARROW_TABS = {
  consultancy: [
    { key: 'home', label: 'Today', ic: 'home', view: 'home' },
    { key: 'tasks', label: 'Tasks', ic: 'tasks', view: 'tasks' },
    { key: 'projects', label: 'Projects', ic: 'projects', view: 'projects' },
    { key: 'more', label: 'More', ic: 'menuLines', view: null },
  ],
  contractor: [
    { key: 'home', label: 'Today', ic: 'home', view: 'home' },
    { key: 'projects', label: 'Projects', ic: 'projects', view: 'projects' },
    { key: 'time', label: 'Time', ic: 'time', view: 'time' },
    { key: 'more', label: 'More', ic: 'menuLines', view: null },
  ],
};

/** The More grid: the rail's groups minus what has a tab, each cell with its live count. PURE. */
export function moreGroups(groups, tabViews, badges = {}) {
  const owned = new Set(tabViews);
  // The rail's unlabelled groups (Settings, Help…) become one group at the end, with no label.
  const named = (groups || []).filter((g) => g.label);
  const rest = (groups || []).filter((g) => !g.label).flatMap((g) => g.items || []);
  return [...named, ...(rest.length ? [{ label: '', items: rest }] : [])]
    .map((g) => ({ label: g.label, items: (g.items || []).filter((it) => !owned.has(it.id)) }))
    .filter((g) => g.items.length)
    .map((g) => ({ label: g.label, cells: g.items.map((it) => {
      const b = badges[it.id];
      const n = b && b.count != null ? Number(b.count) : null;
      const meta = n != null ? { text: b.attn ? `${n} waiting` : String(n), warn: !!b.attn } : (b && b.attn ? { text: 'Needs you', warn: true } : null);
      return { id: it.id, label: it.label, icon: it.icon || it.navIco || it.id, meta };
    }) }));
}

let els = null;
let cfg = null;
const kind = () => (cfg && cfg.contractor && cfg.contractor() ? 'contractor' : 'consultancy');
const tabs = () => {
  const can = (cfg && cfg.canSee) || (() => true);
  return NARROW_TABS[kind()].filter((t) => !t.view || can(t.view));
};

// The header is the phone's own (Release D PR 7, BRIEF §6.6: the desktop at phone
// width takes the same components): navInline's glass bar, the company's disc
// where Back would be, then search and you as the trailing controls. Its data-act
// values are handled here (the phone app's dispatcher is not running on the desktop).
function headerHtml() {
  const who = (cfg.who && cfg.who()) || {};
  const ask = kind() !== 'contractor' && cfg.canAsk && cfg.canAsk();
  const label = ask ? 'Search or ask Track' : 'Search projects';
  return `${navInline({ leadingHtml: companyDisc(who.company || '', who.logo || ''),
    trailingHtml: `${glassBtn(ask ? 'sparkle' : 'search', label, { act: 'nb-search' })}${avatarBtn(who.initials || '·', 'nb-you', 'Your profile and settings')}` })}
    <span class="p4-desk-where" id="mobile-view-name">Home</span>`;
}

function barHtml(active) {
  const t = tabs();
  const tab = (x) => `<button type="button" class="p4-tab${active === x.key ? ' on' : ''}" data-tab="${x.key}"${active === x.key ? ' aria-current="page"' : ''}>${navIcon(x.ic, 20)}<span>${esc(x.label)}</span></button>`;
  return `<nav class="p4-tabbar" aria-label="Tabs">${t.slice(0, 2).map(tab).join('')}
    <button type="button" class="p4-ask p4-add" data-tab="add" aria-label="Add"><i>${navIcon('plus', 24)}</i></button>
    ${t.slice(2, 4).map(tab).join('')}</nav>`;
}

/**
 * Mount the narrow chrome once. cfg: { onSelect(view), groupsFor(), onSearch(),
 * onYou(), addTiles() → [{ label, icon, run }], contractor(), canAsk(),
 * canSee(view), who() → { company, logo, initials } }.
 */
export function mountMobileShell(options) {
  if (els) return els;
  cfg = { ...options };
  const app = document.getElementById('app');
  if (!app) return null;

  // The header: the page's own .mobile-header, redrawn as the phone's.
  const head = app.querySelector('.mobile-header') || app.insertBefore(document.createElement('header'), app.firstChild);
  head.classList.add('mobile-header', 'm-app', 'p4-desk');
  head.innerHTML = headerHtml();
  head.addEventListener('click', (ev) => {
    const search = ev.target.closest('[data-act="nb-search"]');
    const you = ev.target.closest('[data-act="nb-you"]');
    if (search) { closeSheet(); if (cfg.onSearch) cfg.onSearch(); }
    else if (you) { closeSheet(); if (cfg.onYou) cfg.onYou(you); }
  });

  // The bar.
  const bar = document.createElement('div');
  bar.className = 'm-app p4-desk p4-deskbar';
  bar.id = 'tabbar';
  bar.innerHTML = barHtml('home');
  app.appendChild(bar);
  bar.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-tab]');
    if (!b) return;
    const key = b.dataset.tab;
    if (key === 'add') { toggleSheet('add'); return; }
    if (key === 'more') { toggleSheet('more'); return; }
    closeSheet();
    const t = tabs().find((x) => x.key === key);
    if (t && t.view) cfg.onSelect(t.view);
  });

  // The sheet layer (+ Add, More).
  const sheet = document.createElement('div');
  sheet.className = 'm-app p4-desk p4-desksheet';
  sheet.id = 'more-sheet';
  sheet.hidden = true;
  app.appendChild(sheet);
  sheet.addEventListener('click', (ev) => {
    if (ev.target.closest('[data-act="nb-close"]') || ev.target.matches('[data-nb="scrim"]')) { closeSheet(); return; }
    const go = ev.target.closest('[data-go]');
    if (go) { closeSheet(); cfg.onSelect(go.dataset.go); return; }
    const add = ev.target.closest('[data-add]');
    if (add) {
      const tile = ((cfg.addTiles && cfg.addTiles()) || [])[Number(add.dataset.add)];
      closeSheet();
      if (tile && tile.run) tile.run();
    }
  });

  els = { head, bar, sheet };
  // The logo arrives after the first paint (the header shows the lettered tile until then).
  if (cfg.loadLogo) cfg.loadLogo().then(() => { head.innerHTML = headerHtml(); setActiveTab(cfg.currentView); }).catch(() => {});
  return els;
}

/** Repaint the header (the company's name or logo arrived). */
export function refreshHeader() { if (els) { els.head.innerHTML = headerHtml(); setActiveTab(cfg.currentView); } }

function addSheetHtml() {
  const tiles = (cfg.addTiles && cfg.addTiles()) || [];
  return `<div class="p4-desk-scrim" data-nb="scrim"></div>
    <div class="p4-desk-panel" role="dialog" aria-modal="true" aria-label="Add">
      <div class="p4-desk-grab" aria-hidden="true"></div>
      ${sheetHead('Add', { cancel: 'Close', act: 'nb-close' })}
      <div class="p4-addgrid">${tiles.map((t, i) => `<button type="button" class="p4-addtile" data-add="${i}">
        <span class="p4-addtile-ic" aria-hidden="true">${navIcon(t.icon, 18)}</span><span>${esc(t.label)}</span></button>`).join('')}</div>
    </div>`;
}

// More (Release D PR 7): the rail's groups as the phone's inset groups — a row
// each, its live count on the right (orange when it waits on you).
function moreSheetHtml() {
  const groups = moreGroups(cfg.groupsFor ? cfg.groupsFor() : [], tabs().map((t) => t.view).filter(Boolean), cfg.badges || {});
  return `<div class="p4-desk-scrim" data-nb="scrim"></div>
    <div class="p4-desk-panel tall" role="dialog" aria-modal="true" aria-label="More">
      <div class="p4-desk-grab" aria-hidden="true"></div>
      ${sheetHead('More', { cancel: 'Close', act: 'nb-close' })}
      ${groups.map((g) => group(g.cells.map((c) => row({ title: c.label, value: c.meta ? c.meta.text : '', valueLate: !!(c.meta && c.meta.warn), chevron: true,
    leadHtml: `<span class="p4-sym tint" aria-hidden="true">${navIcon(c.icon, 18)}</span>`, attrs: `data-go="${esc(c.id)}" role="link" tabindex="0"` })).join(''),
    { header: g.label, headerStyle: 'caps' })).join('')}
    </div>`;
}

let openKind = null;
function openSheet(which) {
  if (!els) return;
  openKind = which;
  els.sheet.innerHTML = which === 'add' ? addSheetHtml() : moreSheetHtml();
  els.sheet.hidden = false;
  document.body.classList.add('sheet-open');
  paintBar(which === 'more' ? 'more' : activeKey(cfg.currentView));
}
export function closeSheet() {
  if (!els || els.sheet.hidden) return;
  els.sheet.hidden = true;
  els.sheet.innerHTML = '';
  openKind = null;
  document.body.classList.remove('sheet-open');
  paintBar(activeKey(cfg.currentView));
}
function toggleSheet(which) { if (openKind === which) closeSheet(); else openSheet(which); }

/** Which tab lights for a view: its own, else More (everything else lives there). */
export function activeKey(view) {
  const t = tabs().find((x) => x.view === view);
  return t ? t.key : 'more';
}
function paintBar(active) { if (els) els.bar.innerHTML = barHtml(active); }

export function setActiveTab(view) {
  if (!els) return;
  if (view) cfg.currentView = view;
  paintBar(openKind === 'more' ? 'more' : activeKey(cfg.currentView));
}

// app.js hands the live badge counts + the current view in on every refresh.
export function setMobileState({ badges, currentView }) {
  if (!cfg) return;
  if (badges) cfg.badges = badges;
  if (currentView) cfg.currentView = currentView;
  if (els && openKind === 'more') els.sheet.innerHTML = moreSheetHtml();
}

// Kept for callers of the old module: the context pills are gone with the old bar.
export function closeActions() {}
